import { createTheme } from '@material-ui/core/styles';
import Color from 'color';

export default function makeTheme({
  colorMain,
  colorLight,
  fontFamily,
  textDirection,
}) {
  const greyscale = {
    50: '#FAFAFA',
    100: '#F7F7F7',
    200: '#EEEEEE',
    300: '#DFDFDF',
    350: '#BABFC0',
    400: '#939393',
    500: '#7F7F7F',
    600: '#6B6B6B',
    700: '#525252',
    800: '#393939',
    900: '#1C1C1C',
  };

  const clearSky = {
    main: '#00A8EE',
  };

  const autumn = {
    main: '#FF4E4E',
  };

  const summer = {
    main: '#FEBE36',
    extraLight: '#FFF8E8',
  };

  const spring = {
    main: '#6FC922',
  };

  const primaryColor = Color(colorMain).hex();
  const primaryColorLight = colorLight
    ? Color(colorLight).hex()
    : Color(primaryColor).lighten(2.6).desaturate(0.3).hex();
  const primaryPalette = {
    main: primaryColor,
    extraLight: primaryColorLight,
  };

  return createTheme({
    direction: textDirection,
    palette: {
      white: '#ffffff',
      grey: greyscale,
      primary: {
        main: primaryPalette.main,
        extraLight: primaryPalette.extraLight,
      },
      secondary: clearSky,
      error: autumn,
      success: spring,
      warning: summer,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 930,
        xl: 1280,
      },
    },
    spacing: 10,
    typography: {
      fontFamily,
      h1: {
        fontWeight: 600,
        fontSize: 16,
        color: greyscale[700],
        lineHeight: '22px',
      },
      h2: {
        color: greyscale[700],
        fontWeight: 600,
        fontSize: 26,
        lineHeight: '36px',
        marginBottom: 28,
        position: 'relative',
        textAlign: 'center',

        '&[underline="true"]': {
          paddingBottom: 28,

          '&:after': {
            display: 'block',
            content: '""',
            position: 'absolute',
            width: 96,
            height: 4,
            borderRadius: 2,
            backgroundColor: primaryPalette.main,
            left: 0,
            right: 0,
            bottom: 0,
            margin: '0 auto',
          },
        },
      },
      h3: {
        fontWeight: 400,
        fontSize: 16,
        color: greyscale[800],
        lineHeight: 1.8,
      },
      h4: {
        fontWeight: 500,
        fontSize: 16,
        marginBottom: 50,
        color: greyscale[800],
        lineHeight: 1.6,
      },
      h5: {
        fontWeight: 400,
        fontSize: 16,
        color: greyscale[700],
        lineHeight: '16px',
      },
      body1: {
        fontSize: 16,
        marginBottom: 20,
      },
      body2: {
        fontWeight: 500,
        fontSize: 13,
        color: greyscale[600],
        lineHeight: 1.6,
      },
    },
    overrides: {
      MuiButton: {
        root: {
          fontSize: 16,
          margin: '5px 0',
          minHeight: '48px',
          minWidth: '100px',
          borderRadius: '4px',
          textTransform: 'none',

          '&$disabled': {
            borderColor: '#eee',
            backgroundColor: '#eee',
            color: greyscale[400],
          },
          '&:hover': {
            'background-color': primaryPalette.extraLight,
          },
        },
        text: {
          color: primaryPalette.main,
          backgroundColor: 'transparent',

          '&:focus': {
            backgroundColor: primaryPalette.extraLight,
          },
        },
        outlined: {
          border: `1px solid ${primaryPalette.main}`,
          backgroundColor: 'transparent',
        },
        contained: {
          backgroundColor: 'white',
          color: greyscale[600],
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.24)',
        },
        containedPrimary: {
          color: 'white',
          backgroundColor: primaryPalette.main,
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: 'inherit',
            },
          },
        },
        sizeSmall: {
          padding: '8px 12px',
          fontWeight: 600,
          margin: 0,
          minHeight: 0,

          '& svg': {
            fontSize: '20px',
          },
        },
        disabled: {},
      },
      MuiLinearProgress: {
        root: {
          width: '100%',
          backgroundColor: primaryPalette.extraLight,
          'border-radius': '5px',
          'box-shadow': 'inset 0px 2px 0px rgba(0,0,0,0.1)',
          height: '10px',
        },
        bar: {
          backgroundColor: primaryPalette.main,
          'border-radius': '5px',
        },
      },
      MuiListItem: {
        root: {
          backgroundColor: '#fff',
        },
        divider: {
          borderBottom: '1px solid rgb(223, 222, 222)',
        },
        button: {
          '&:hover': {
            backgroundColor: greyscale[100],
          },
        },
      },
      MuiInput: {
        root: {
          '&:after, &:before': {
            display: 'none',
          },
        },
      },
      MuiInputBase: {
        root: {
          lineHeight: '24px',

          '&$disabled': {
            boxShadow: 'none !important',
          },

          '&$error': {
            borderColor: `${autumn.main} !important`,
          },
        },
        input: {
          boxSizing: 'border-box',
        },
      },
      MuiSelect: {
        root: {
          height: '100%',
        },

        selectMenu: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: 16,
        },

        select: {
          width: '100%',
          height: '100%',
          paddingLeft: 16,
        },
      },
      MuiRadio: {
        colorSecondary: {
          color: primaryPalette.main,
          '&.Mui-checked': {
            color: primaryPalette.main,
          },
        },
      },
      MuiCheckbox: {
        colorSecondary: {
          color: primaryPalette.main,
          '&.Mui-checked': {
            color: primaryPalette.main,
          },
        },
      },
      MuiFormControl: {
        root: {
          width: '100%',

          '& > div:not(label)': {
            // This is essentially just the input component inside the `FromControl`.
            marginTop: '24px',
            border: `1px solid ${greyscale[350]}`,
            borderRadius: 4,
            height: 48,
            overflow: 'hidden',

            '& > input': {
              width: '100%',
              height: '100%',
              padding: '0 16px',
              border: 'none',
              background: 'transparent',

              '&:focus': {
                background: 'rgba(0, 0, 0, 0.05)',
              },
            },
          },

          '& > label': {
            color: `${greyscale[700]} !important`,
            width: '130%',
          },
        },
      },
      MuiFormHelperText: {
        root: {
          color: autumn.main,
          marginTop: -15,
        },
      },
      MuiTypography: {
        gutterBottom: {
          marginBottom: '1em',
        },
      },
    },
  });
}
